import type { SimpleChanges } from '@angular/core';
import { Component, Input, ChangeDetectorRef } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs';

import { BulkActionsToolbarComponent } from '@recruitee/bulk-actions';
import { filterBoolean, trackByIndex } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { RequisitionsFilterCounters } from '@recruitee/requisitions-data';
import type { RequisitionsParams } from '@recruitee/requisitions-types';
import {
  RequisitionsGroupKind,
  RequisitionsListParam,
  RequisitionsScope,
} from '@recruitee/requisitions-types';
import type { SegmentedButtonType } from '@recruitee/user-interface';
import {
  ButtonComponent,
  FormFieldComponent,
  GroupComponent,
  IconComponent,
  InputComponent,
  SegmentedControlComponent,
  TabComponent,
  TabsComponent,
  DropdownWidth,
  DataTableOptionsButtonComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  ConnectedOverlayDirective,
  OverlayOriginDirective,
  ButtonDropdownIndicatorDirective,
} from '@recruitee/user-interface';
import { LifeCycle } from '@recruitee/utils';

import { RequisitionsAnalytics } from '../../../analytics/requisitions.analytics';
import { RequisitionsListFacade } from '../../../facades/list/requisitions-list.facade';
import type { RequisitionsTableColumn } from '../../../requisitions.types';
import { RequisitionsListTab, RequisitionsListViewType } from '../../../requisitions.types';
import { RequisitionsCreateService } from '../../../services/requisitions-create.service';
import { RequisitionsListGroupsService } from '../../../services/requisitions-list-groups.service';
import type { RequisitionsTableConfig } from '../../../services/requisitions-list-table.service';
import { RequisitionsListTableService } from '../../../services/requisitions-list-table.service';
import { RequisitionsListViewStateService } from '../../../services/requisitions-list-view-state.service';

@Component({
  selector: 'rt-requisitions-list-toolbar-container',
  templateUrl: './requisitions-list-toolbar-container.component.html',
  styleUrls: ['./requisitions-list-toolbar-container.component.less'],
  imports: [
    ButtonComponent,
    FormFieldComponent,
    GroupComponent,
    IconComponent,
    InputComponent,
    SegmentedControlComponent,
    TabComponent,
    TabsComponent,
    TranslatePipe,
    ReactiveFormsModule,
    DataTableOptionsButtonComponent,
    DropdownComponent,
    DropdownHeaderComponent,
    DropdownItemComponent,
    ConnectedOverlayDirective,
    OverlayOriginDirective,
    ButtonDropdownIndicatorDirective,
    BulkActionsToolbarComponent,
  ],
})
export class RequisitionsListToolbarContainerComponent {
  @Input() public params: RequisitionsParams;
  @Input() public filterCounters: RequisitionsFilterCounters;
  @Input() public newRequisitionsDisabled: boolean;
  @Input() public hasRequisitions: boolean;
  @Input() public activeTab: RequisitionsListTab;
  @Input() public viewType: RequisitionsListViewType;

  public RequisitionsScope: typeof RequisitionsScope = RequisitionsScope;
  public RequisitionsListTab: typeof RequisitionsListTab = RequisitionsListTab;
  public RequisitionsGroupKind: typeof RequisitionsGroupKind = RequisitionsGroupKind;
  public RequisitionsListViewType: typeof RequisitionsListViewType = RequisitionsListViewType;
  public RequisitionsListParam: typeof RequisitionsListParam = RequisitionsListParam;
  public DropdownWidth: typeof DropdownWidth = DropdownWidth;
  public trackByIndex = trackByIndex;
  public groups = this.requisitionsListGroupsService.groupByItems;
  public canCreateRequisitions: boolean;
  public queryControl = new FormControl('', {
    nonNullable: true,
  });
  public segmentedViewTypeButtons: Array<SegmentedButtonType<RequisitionsListViewType>> = [];
  public groupByButtonLabel: string;
  public tableConfig: RequisitionsTableConfig;
  public groupBy: RequisitionsGroupKind | null = null;

  @LifeCycle('onDestroy')
  private ngOnDestroy$: Subject<void>;

  constructor(
    private requisitionsListFacade: RequisitionsListFacade,
    private createRequisitionService: RequisitionsCreateService,
    private requisitionsViewState: RequisitionsListViewStateService,
    private requisitionsListGroupsService: RequisitionsListGroupsService,
    private requisitionsListTableService: RequisitionsListTableService,
    private i18n: I18nService,
    private cdRef: ChangeDetectorRef,
    private requisitionsAnalytics: RequisitionsAnalytics,
  ) {}

  public ngOnInit(): void {
    this.subscribeToQueryChanges();

    this.requisitionsListTableService.tableConfig$
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(config => {
        this.tableConfig = config;
        this.cdRef.detectChanges();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.canCreateRequisitions = this.requisitionsListFacade.canCreateRequisitions;
    if (changes.params?.firstChange) {
      this.queryControl.setValue(this.params.q ?? '', { emitEvent: false });
    }

    if (changes.params?.currentValue) {
      this.groupBy = this.params?.[RequisitionsListParam.GroupBy] || null;
      this.groupByButtonLabel = this.requisitionsListGroupsService.getGroupButtonLabel(
        this.groupBy,
      );
    }

    if (changes.viewType?.currentValue) {
      this.setViewTypeSegmentButtons();
    }
  }

  public get segmentedFilterButtons(): Array<SegmentedButtonType<RequisitionsScope>> {
    return [
      {
        isActive: this.params.scope === RequisitionsScope.Active,
        value: RequisitionsScope.Active,
        label: this.i18n.translate('app.common.all'),
        counter: this.filterCounters?.active,
        isAvailable: true,
      },
      {
        isActive: this.params.scope === RequisitionsScope.NeedMyApproval,
        value: RequisitionsScope.NeedMyApproval,
        label: this.i18n.translate('app.requisitions.list.need_my_approval'),
        counter: this.filterCounters?.needMyApproval,
        isAvailable: true,
      },
      {
        isActive: this.params.scope === RequisitionsScope.RequestedByMe,
        value: RequisitionsScope.RequestedByMe,
        label: this.i18n.translate('app.requisitions.list.requested_by_me'),
        counter: this.filterCounters?.requestedByMe,
        isAvailable: true,
      },
    ];
  }

  public setViewTypeSegmentButtons(): void {
    this.segmentedViewTypeButtons = [
      {
        isActive: this.viewType === RequisitionsListViewType.Tiles,
        value: RequisitionsListViewType.Tiles,
        iconName: 'view-stream',
        isAvailable: true,
      },
      {
        isActive: this.viewType === RequisitionsListViewType.Table,
        value: RequisitionsListViewType.Table,
        iconName: 'view-column',
        isAvailable: true,
      },
    ];
  }

  public changeScope(scope: RequisitionsScope): void {
    this.requisitionsViewState.changeParams({ scope });
    this.requisitionsAnalytics.trackChangeScope(scope);
  }

  public changeViewType(viewType: RequisitionsListViewType): void {
    this.requisitionsViewState.setViewType(viewType);
    this.requisitionsAnalytics.trackViewType(viewType);
  }

  public changeGroup(groupBy: RequisitionsGroupKind | null): void {
    this.requisitionsViewState.setGroup(groupBy);
    this.requisitionsAnalytics.trackGroupBy(groupBy);
  }

  public changeTableConfig(config: {
    autoFit?: boolean;
    columns?: RequisitionsTableColumn[];
  }): void {
    if (config.columns) this.trackAdjustedColumn(config.columns);
    this.requisitionsListTableService.changeTableConfig(config);
  }

  public get isNewRequisitionsButtonVisible(): boolean {
    return (
      this.activeTab === RequisitionsListTab.Active &&
      this.canCreateRequisitions &&
      this.hasRequisitions
    );
  }

  public newRequisition() {
    this.createRequisitionService.create();
  }

  private subscribeToQueryChanges(): void {
    this.queryControl.valueChanges
      .pipe(debounceTime(300), takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(query => {
        this.requisitionsViewState.changeParams({ q: query });
      });
  }

  private trackAdjustedColumn(visibleColumns: RequisitionsTableColumn[]) {
    const selectedFiled = visibleColumns.find(column =>
      this.requisitionsListTableService.table.allColumns.find(
        c => c.id === column.id && c.visible !== column.visible,
      ),
    )!;
    const enabled = !!selectedFiled.visible;

    this.requisitionsAnalytics.trackAdjustColumns(selectedFiled.id, enabled);
  }
}
